import React, { useMemo, useState } from "react";
import { Image, Space } from "antd";
import { StyledContainer, StyledContent, TypeText, StyledGallery } from "../../routes/styles";
import { DownloadOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { StyledPageBtn } from "./styles";
import { MdNavigateBefore, MdNavigateNext } from "react-icons/md";
import { VehicleImageData } from "../VehicleImage";
import { CountDamageType } from "../../types";

type UploadedFile = {
  file: File;
  folderPath: string;
};

interface ResultCarImageProps {
  data: CountDamageType[] | undefined;
  uploadedFiles: UploadedFile[];
}

export const ResultCarImage: React.FC<ResultCarImageProps> = React.memo(({ data, uploadedFiles }) => {
  const carDataBefore = useMemo(() => uploadedFiles, [uploadedFiles]);
  const carDataAfter = data;
  const imagesPerPage = 4;
  
  const [page, setPage] = useState(0);
  if(!carDataAfter) return;

  // 페이지 수 계산
  const totalAfterPages = Math.ceil(carDataAfter.length / imagesPerPage);

  // 현재 페이지에 표시될 이미지들
  const beforeImagesToShow = carDataBefore.slice(page * imagesPerPage, (page + 1) * imagesPerPage);
  const afterImagesToShow = carDataAfter.slice(page * imagesPerPage, (page + 1) * imagesPerPage);

  const onDownload = (data: File | string, isBefore: boolean, path?: string) => {
    if (!data) return;

    if (isBefore) {
      const file = data as File;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(file);
      link.download = file.name || "image.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const url = `data:image/jpeg;base64,${data as string}`;
      console.log(url)
      const link = document.createElement("a");
      link.href = url;
      link.download = path ? `${path}` : "image.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <StyledContent style={{ marginBottom: "2vw" }}>
      <StyledContainer style={{ justifyContent: "flex-start" }}>
        <TypeText>Original Image</TypeText>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "row",
            boxSizing: "border-box",
          }}
        >
          <StyledPageBtn
              style={{ color: page === 0 ? "rgba(255,255,255,0.3)" : "white" }}
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={page === 0}
            >
              <MdNavigateBefore />
            </StyledPageBtn>
          <StyledGallery>
            <Image.PreviewGroup
              preview={{
                toolbarRender: (_, { transform: { scale }, actions: { onZoomIn, onZoomOut }, current }) => (
                  <Space size={12} className="toolbar-wrapper">
                    <DownloadOutlined
                      style={{ fontSize: "2rem" }}
                      onClick={() => onDownload(carDataBefore?.[current].file, true)}
                    />
                    <ZoomOutOutlined
                      style={{ fontSize: "2rem" }}
                      disabled={scale === 1}
                      onClick={onZoomOut}
                    />
                    <ZoomInOutlined
                      style={{ fontSize: "2rem" }}
                      disabled={scale === 50}
                      onClick={onZoomIn}
                    />
                  </Space>
                ),
              }}
            >
              {beforeImagesToShow.map((img, index) => (
                <VehicleImageData key={index} data={img.file} isBeforeImage={true} />
              ))}
            </Image.PreviewGroup>
          </StyledGallery>
        </div>
      </StyledContainer>

      <StyledContainer style={{ justifyContent: "flex-start" }}>
        <TypeText>Processed Image</TypeText>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          <StyledGallery>
            <Image.PreviewGroup
              preview={{
                toolbarRender: (_, { transform: { scale }, actions: { onZoomIn, onZoomOut }, current }) => (
                  <Space size={12} className="toolbar-wrapper">
                    <DownloadOutlined
                      style={{ fontSize: "2rem" }}
                      onClick={() => onDownload(carDataAfter?.[current].images, false, carDataBefore?.[current].file.name)}
                    />
                    <ZoomOutOutlined
                      style={{ fontSize: "2rem" }}
                      disabled={scale === 1}
                      onClick={onZoomOut}
                    />
                    <ZoomInOutlined
                      style={{ fontSize: "2rem" }}
                      disabled={scale === 50}
                      onClick={onZoomIn}
                    />
                  </Space>
                ),
              }}
            >
              {afterImagesToShow.map((img, index) => (
                <VehicleImageData key={index} data={img} isBeforeImage={false} />
              ))}
            </Image.PreviewGroup>
          </StyledGallery>
          <StyledPageBtn
              style={{
                color: page >= totalAfterPages - 1 ? "rgba(255,255,255,0.3)" : "white",
              }}
              onClick={() => setPage((prev) => Math.min(prev + 1, totalAfterPages - 1))}
              disabled={page >= totalAfterPages - 1}
            >
              <MdNavigateNext />
            </StyledPageBtn>
        </div>
      </StyledContainer>
    </StyledContent>
  );
});